import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, CheckCircle, ArrowLeft, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RepairQuote = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    // Contact Info
    name: '',
    email: '',
    phone: '',
    
    // Machine Info
    brand: '',
    model: '',
    size: '',
    serial_number: '',
    purchase_date: '',
    
    // Issue Details
    issue_type: '',
    issue_description: '',
    error_codes: '',
    
    // Additional
    preferred_contact: 'email',
    urgency: 'standard'
  });

  const machinesBrands = [
    'HomeLyph',
    'Harvest Right',
    'Stay Fresh',
    'Blue Alpine',
    'Other'
  ];

  const machineSizes = [
    'Small / Compact',
    'Medium / Standard',
    'Large / Pro',
    'Extra Large / Commercial',
    'Industrial',
    'Not Sure'
  ];

  const issueTypes = [
    'Vacuum pump not working',
    'Not reaching temperature',
    'Cycle not completing',
    'Error code displayed',
    'Strange noises',
    'Leaking',
    'Control panel issues',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/repair-quotes`, formData);
      setSubmitted(true);
      toast.success('Repair quote request submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#f0f4f8]">
        <Navbar />
        <div className="pt-36 pb-20">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="bg-white rounded-2xl p-12 shadow-sm">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-[#0a0203] mb-4">Request Received!</h1>
              <p className="text-gray-600 mb-8">
                Thank you for your repair quote request. Our team will review your information and 
                contact you within 1-2 business days with a repair estimate.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/repair">
                  <Button variant="outline">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back to Repair Services
                  </Button>
                </Link>
                <a href="https://www.lyphlinefreezedry.com/" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#003456]">
                    Visit LyphLine
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f0f4f8]" data-testid="repair-quote-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-36 pb-12 bg-gradient-to-br from-[#003456] to-[#001d30]">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/repair" className="inline-flex items-center text-white/70 hover:text-white mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Repair Services
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-[#fdc31c] rounded-xl flex items-center justify-center">
              <Wrench className="h-7 w-7 text-[#0a0203]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">Request Repair Quote</h1>
              <p className="text-white/70">Tell us about your freeze dryer</p>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* Contact Information */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-xl font-bold text-[#0a0203] mb-6">Contact Information</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    required
                    data-testid="repair-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    required
                    data-testid="repair-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    data-testid="repair-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Contact Method</label>
                  <select
                    name="preferred_contact"
                    value={formData.preferred_contact}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-transparent"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="text">Text Message</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Machine Information */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-xl font-bold text-[#0a0203] mb-6">Machine Information</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Brand / Manufacturer *</label>
                  <select
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-transparent"
                    data-testid="repair-brand"
                  >
                    <option value="">Select brand...</option>
                    {machinesBrands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Model Name/Number</label>
                  <Input
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    placeholder="e.g., Pro XL, Standard, etc."
                    data-testid="repair-model"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Machine Size *</label>
                  <select
                    name="size"
                    value={formData.size}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-transparent"
                    data-testid="repair-size"
                  >
                    <option value="">Select size...</option>
                    {machineSizes.map(size => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Serial Number</label>
                  <Input
                    name="serial_number"
                    value={formData.serial_number}
                    onChange={handleChange}
                    placeholder="If available"
                    data-testid="repair-serial"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Approximate Purchase Date</label>
                  <Input
                    type="month"
                    name="purchase_date"
                    value={formData.purchase_date}
                    onChange={handleChange}
                    data-testid="repair-purchase-date"
                  />
                </div>
              </div>
            </div>

            {/* Issue Details */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="text-xl font-bold text-[#0a0203] mb-6">Issue Details</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type of Issue *</label>
                  <select
                    name="issue_type"
                    value={formData.issue_type}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-transparent"
                    data-testid="repair-issue-type"
                  >
                    <option value="">Select issue type...</option>
                    {issueTypes.map(issue => (
                      <option key={issue} value={issue}>{issue}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Describe the Problem *</label>
                  <textarea
                    name="issue_description"
                    value={formData.issue_description}
                    onChange={handleChange}
                    placeholder="Please describe the issue in detail. When did it start? What happens when you try to run a cycle? Any unusual sounds or behaviors?"
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-transparent resize-none"
                    data-testid="repair-description"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Error Codes (if any)</label>
                  <Input
                    name="error_codes"
                    value={formData.error_codes}
                    onChange={handleChange}
                    placeholder="e.g., E01, E05, etc."
                    data-testid="repair-error-codes"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Urgency Level</label>
                  <div className="flex flex-wrap gap-4">
                    {[
                      { value: 'standard', label: 'Standard', desc: '5-10 business days' },
                      { value: 'priority', label: 'Priority', desc: '3-5 business days' },
                      { value: 'urgent', label: 'Urgent', desc: '1-2 business days' }
                    ].map(option => (
                      <label 
                        key={option.value}
                        className={`flex-1 min-w-[150px] p-4 border-2 rounded-xl cursor-pointer transition-colors ${
                          formData.urgency === option.value 
                            ? 'border-[#003456] bg-[#f0f8ff]' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="urgency"
                          value={option.value}
                          checked={formData.urgency === option.value}
                          onChange={handleChange}
                          className="sr-only"
                        />
                        <span className="font-semibold text-[#0a0203]">{option.label}</span>
                        <span className="block text-sm text-gray-500">{option.desc}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                * Required fields
              </p>
              <Button 
                type="submit" 
                disabled={loading}
                className="bg-[#003456] hover:bg-[#001d30] px-8 py-6 text-lg"
                data-testid="repair-submit"
              >
                {loading ? 'Submitting...' : (
                  <>
                    Submit Request <Send className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RepairQuote;
